import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import { sendOtpAction, tempVerifyOtpAction, verifyOtpAction } from "../../redux/actions/noticeToVacate";
import { useTranslation } from "react-i18next";

const VerifyOtp = ({
  zzID_PEPL,
  sentTo,
  showVerifyOtpModal,
  setShowVerifyOtpModal,
  handleVerifyOtp,
  tenantNotFound,
  _id,
  sentOn,
  email,
  phone,
  setData
}) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState("");
  const [selectedLease, setSelectedLease] = useState("");
  const [leaseOptions, setLeaseOptions] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [resendDisabled, setResendDisabled] = useState(true);
  const [countdown, setCountdown] = useState(60);
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    if (showVerifyOtpModal) {
      startTimer();
    } else {
      stopTimer();
    }
    return () => stopTimer();
  }, [showVerifyOtpModal]);

  const startTimer = () => {
    stopTimer();
    setCountdown(60);
    setResendDisabled(true);

    const newTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopTimer();
          setResendDisabled(false);
          return 60;
        }
        return prev - 1;
      });
    }, 1000);
    setTimer(newTimer);
  };

  const stopTimer = () => {
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }
  };

  const handleVerify = async () => {
    try {
      if (!otp) {
        setError(t('tenantApp.keyReturn.OTPRequired'));
        return;
      }
      setError("");
      setLoading(true);
      const result = await dispatch(
        verifyOtpAction({
          zzID_PEPL,
          otp,
          sentOn
        })
      );

      if (Array.isArray(result.data)) {
        if (result.data.length === 0) {
          handleVerifyOtp({zzID_PEPL})
        } else if (result.data.length === 1) {
          const lease = result.data[0]?.fieldData;
          handleVerifyOtp({zzID_PEPL, zzID_LESE: lease?.zzID})
        } else {
          setLeaseOptions(
            result.data.map(({ fieldData }) => ({
              id: fieldData?.zzID,
              address:`${fieldData?.["Lease_PROP::Address_Address01_finalDisplay"]} ${fieldData?.["Lease_PROP::Address_City"]}, ${fieldData?.["Lease_PROP::Address_State"]} - ${fieldData?.["Lease_UNIT::Number"]}`,
            }))
          );
        }
      } else {
        handleVerifyOtp({zzID_PEPL})
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTempVerify = async () => {
    try {
      if (!otp) {
        setError(t('tenantApp.keyReturn.OTPRequired'));
        return;
      }
      setError("");
      setLoading(true);
      const result = await dispatch(
        tempVerifyOtpAction({
          _id,
          otp,
        })
      );
      if(result){
        handleVerifyOtp({tenantNotFound});
      }
    } catch (error) {
      console.error(error)
    }finally {
      setLoading(false);
    }
  }

  const handleLeaseChange = (e) => {
    const leaseId = e.target.value;
    setSelectedLease(leaseId);
    if (leaseId) {
      handleVerifyOtp({zzID_PEPL, zzID_LESE: leaseId})
    }
  };

  const handleReSendOTP = async () => {
    if (resendDisabled) return;
    try {
      setLoading(true);
      const result = await dispatch(
        sendOtpAction({
          email: email,
          Phone_Mobile: phone,
        })
      );
      setData && setData(result);
      startTimer();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={showVerifyOtpModal} centered>
      <ModalHeader>
        {`A pin was sent to ${sentTo}. Please confirm here`}
      </ModalHeader>
      <ModalBody className="bg-white">
        <div className="form-group">
          <label className="form-label">{t('tenantApp.keyReturn.OneTimePin')}</label>
          <input
            type="text"
            className={`form-control ${error ? "is-invalid" : ""}`}
            placeholder={t('tenantApp.keyReturn.EnterOTP')}
            value={otp}
            onChange={(e) => {
              let x = e.target.value.replace(/\D/g, "");
              setOtp(x);
              setError("");
            }}
            disabled={leaseOptions.length > 0}
            maxLength={6}
          />
          {error && <div className="text-danger">{error}</div>}
        </div>
        {leaseOptions.length > 0 && (
          <div className="form-group mt-3">
            <label className="form-label">
              {t('tenantApp.keyReturn.WeFoundMultipleLease')}
            </label>
            <select
              className="form-control"
              value={selectedLease}
              onChange={handleLeaseChange}
            >
              <option value="">{t('tenantApp.keyReturn.SelectAlease')}</option>
              {leaseOptions.map((lease) => (
                <option key={lease.id} value={lease.id}>
                  {lease.address}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="float-right">
        {leaseOptions.length === 0 && (
        <button
          className="btn btn-primary mr-3"
          onClick={handleReSendOTP}
          disabled={resendDisabled || loading}
        >
          {`${sentOn === "email" ? t('tenantApp.keyReturn.sendPin') : t('tenantApp.keyReturn.ResendOTP')}${resendDisabled ? ` (${countdown}s)` : ''}`}
        </button>
        )}
          {!leaseOptions.length ? (
            <button
              className="btn btn-primary mr-3"
              onClick={tenantNotFound ? handleTempVerify : handleVerify}
              disabled={loading}
            >
              {loading ? (
                <Spinner color="white" size="sm">
                  Loading...
                </Spinner>
              ) : (
                t('tenantApp.keyReturn.VerifyOTP')
              )}
            </button>
          ) : null}
          <button
            className="btn btn-secondary "
            onClick={() => {
              setOtp("");
              setError("");
              setShowVerifyOtpModal(false);
              stopTimer(); 
            }}
            disabled={loading}
          >
            {t('tenantApp.keyReturn.close')}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default VerifyOtp;
